import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  getPropertyById,
  toggleSparkSyncSuspension,
} from 'src/services/client';

interface SparkSyncSuspendEnableState {
  isSpark: boolean;
  sparkSyncSuspended: boolean;
  isTogglingSuspend: boolean;
  isLoading: boolean;
  error: string | null;
  isConfirmModalOpen: boolean;
}

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'warning' | 'danger' | 'info' | 'confirmation';
  requireTypeToConfirm: boolean;
  confirmationWord: string;
}

export interface UseSparkSyncSuspendEnableReturn extends SparkSyncSuspendEnableState {
  openSuspendConfirmModal: () => void;
  closeConfirmModal: () => void;
  confirmSuspendAction: () => Promise<void>;
  confirmModalProps: ConfirmModalProps;
  refetch: () => Promise<void>;
}

const toastConfig = {
  autoClose: 5000,
  position: 'top-center' as const,
};

const useSparkSyncSuspendEnable = (
  propertyId: string | undefined
): UseSparkSyncSuspendEnableReturn => {
  const [state, setState] = useState<SparkSyncSuspendEnableState>({
    isSpark: false,
    sparkSyncSuspended: false,
    isTogglingSuspend: false,
    isLoading: false,
    error: null,
    isConfirmModalOpen: false,
  });

  const fetchPropertyData = useCallback(async () => {
    if (!propertyId) return;

    setState((prev: SparkSyncSuspendEnableState) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response: any = await getPropertyById(propertyId);

      if (response.ok || response.status === 200) {
        const data: any = await response.json();

        setState((prev: SparkSyncSuspendEnableState) => ({
          ...prev,
          isSpark: data.isSpark ?? false,
          sparkSyncSuspended: data.sparkSyncSuspended ?? false,
          isLoading: false,
        }));
      } else {
        setState((prev: SparkSyncSuspendEnableState) => ({
          ...prev,
          isLoading: false,
          error: `Failed to fetch property: ${response.status}`,
        }));
      }
    } catch (error) {
      console.error('Error fetching property by ID:', error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'Error fetching property data',
      }));
    }
  }, [propertyId]);

  useEffect(() => {
    fetchPropertyData();
  }, [fetchPropertyData]);

  const openSuspendConfirmModal = useCallback(() => {
    setState((prev: SparkSyncSuspendEnableState) => ({
      ...prev,
      isConfirmModalOpen: true,
    }));
  }, []);

  const closeConfirmModal = useCallback(() => {
    setState((prev: SparkSyncSuspendEnableState) => ({
      ...prev,
      isConfirmModalOpen: false,
    }));
  }, []);

  const confirmSuspendAction = useCallback(async () => {
    if (state.isTogglingSuspend || !propertyId) {
      return;
    }

    const newSuspendedState: boolean = !state.sparkSyncSuspended;

    setState((prev: SparkSyncSuspendEnableState) => ({
      ...prev,
      isTogglingSuspend: true,
      error: null,
    }));

    try {
      const response: any = await toggleSparkSyncSuspension(
        propertyId,
        newSuspendedState
      );

      if (response.ok || response.status === 200) {
        setState((prev: SparkSyncSuspendEnableState) => ({
          ...prev,
          sparkSyncSuspended: newSuspendedState,
          isConfirmModalOpen: false,
        }));
        toast.success(
          newSuspendedState
            ? 'Spark sync suspended successfully.'
            : 'Spark sync reactivated successfully.',
          toastConfig
        );
      } else {
        toast.error(
          'Failed to update sync status. Please try again.',
          toastConfig
        );
        setState((prev: SparkSyncSuspendEnableState) => ({
          ...prev,
          error: 'Failed to toggle Spark sync suspension',
        }));
      }
    } catch (error) {
      console.error('Error toggling Spark sync suspension:', error);
      toast.error(
        'Failed to update sync status. Please try again.',
        toastConfig
      );
      setState((prev: SparkSyncSuspendEnableState) => ({
        ...prev,
        error: 'Error toggling Spark sync suspension',
      }));
    } finally {
      setState((prev: SparkSyncSuspendEnableState) => ({
        ...prev,
        isTogglingSuspend: false,
      }));
    }
  }, [propertyId, state.isTogglingSuspend, state.sparkSyncSuspended]);

  const confirmModalProps: ConfirmModalProps = state.sparkSyncSuspended
    ? {
        title: 'Reactivate Spark Sync',
        message:
          'Are you sure you want to reactivate Spark sync for this property? Data will resume syncing from Spark to Juniper for this property. Where information differs between Spark and Juniper, Juniper data will be overwritten with Spark data.',
        confirmLabel: 'Enable Sync',
        cancelLabel: 'Cancel',
        variant: 'confirmation',
        requireTypeToConfirm: true,
        confirmationWord: 'ENABLE SYNC',
      }
    : {
        title: 'Suspend Spark Sync',
        message:
          'Are you sure you want to suspend the Spark sync for this property? Data will no longer sync for this property from Spark to Juniper, and Juniper data may become out of date until sync is reactivated.',
        confirmLabel: 'Suspend Sync',
        cancelLabel: 'Cancel',
        variant: 'confirmation',
        requireTypeToConfirm: true,
        confirmationWord: 'SUSPEND SYNC',
      };

  return {
    ...state,
    openSuspendConfirmModal,
    closeConfirmModal,
    confirmSuspendAction,
    confirmModalProps,
    refetch: fetchPropertyData,
  };
};

export default useSparkSyncSuspendEnable;
